import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { on } from '@ember/modifier';
import type Owner from '@ember/owner';
import Icon from './icon';
import XIcon from '~icons/lucide/x';
import tw from '../helpers/tw';

type DropdownPlacement =
  | 'dropdown-start'
  | 'dropdown-end'
  | 'dropdown-center'
  | 'dropdown-top'
  | 'dropdown-bottom'
  | 'dropdown-left'
  | 'dropdown-right';

interface AdaptiveMenuSignature {
  Args: {
    /** Dropdown placement on desktop (e.g. 'dropdown-end') */
    placement?: DropdownPlacement;
    /** Title shown in the mobile drawer header */
    title?: string;
    /** Extra classes for the dropdown content container (desktop) */
    contentClass?: string;
    /** Callback fired whenever the menu closes */
    onClose?: () => void;
  };
  Blocks: {
    trigger: [toggle: () => void, isOpen: boolean];
    content: [close: () => void];
  };
}

const LG_BREAKPOINT = '(max-width: 1023px)';

export default class AdaptiveMenu extends Component<AdaptiveMenuSignature> {
  @tracked isOpen = false;
  @tracked isMobile = false;

  private mediaQuery: MediaQueryList | null = null;
  private usingLegacyMediaQueryListener = false;
  private touchStartY = 0;
  private dragOffset = 0;

  constructor(owner: Owner, args: AdaptiveMenuSignature['Args']) {
    super(owner, args);
    if (typeof window !== 'undefined') {
      this.mediaQuery = window.matchMedia(LG_BREAKPOINT);
      this.isMobile = this.mediaQuery.matches;
      const mediaQueryWithLegacy = this.mediaQuery as MediaQueryList & {
        addListener?: (listener: (event: MediaQueryListEvent) => void) => void;
      };
      // Android WebView compatibility: some engines still expose addListener/removeListener only.
      if (typeof this.mediaQuery.addEventListener === 'function') {
        this.mediaQuery.addEventListener('change', this.handleMediaChange);
      } else if (typeof mediaQueryWithLegacy.addListener === 'function') {
        this.usingLegacyMediaQueryListener = true;
        mediaQueryWithLegacy.addListener(this.handleMediaChange);
      }
    }
  }

  willDestroy(): void {
    super.willDestroy();
    if (!this.mediaQuery) return;

    if (this.usingLegacyMediaQueryListener) {
      const mediaQueryWithLegacy = this.mediaQuery as MediaQueryList & {
        removeListener?: (
          listener: (event: MediaQueryListEvent) => void
        ) => void;
      };
      mediaQueryWithLegacy.removeListener?.(this.handleMediaChange);
      return;
    }

    this.mediaQuery.removeEventListener('change', this.handleMediaChange);
  }

  private handleMediaChange = (event: MediaQueryListEvent) => {
    this.isMobile = event.matches;
    if (!this.isMobile && this.isOpen) {
      this.isOpen = false;
    }
  };

  @action
  toggle() {
    this.isOpen = !this.isOpen;
    if (!this.isOpen) {
      this.args.onClose?.();
    }
  }

  @action
  close() {
    this.isOpen = false;
    this.args.onClose?.();
  }

  @action
  onBackdropKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.close();
    }
  }

  @action
  onTouchStart(event: TouchEvent) {
    if (event.touches.length === 0) return;
    this.touchStartY = event.touches[0]!.clientY;
    this.dragOffset = 0;
  }

  @action
  onTouchMove(event: TouchEvent) {
    if (event.touches.length === 0) return;
    const delta = event.touches[0]!.clientY - this.touchStartY;
    this.dragOffset = Math.max(0, delta);
  }

  @action
  onTouchEnd() {
    if (this.dragOffset > 80) {
      this.close();
    }
    this.dragOffset = 0;
  }

  <template>
    {{#if this.isMobile}}
      {{! Mobile: trigger button (inline, no wrapper) }}
      {{yield this.toggle this.isOpen to="trigger"}}

      {{#if this.isOpen}}
        {{! Mobile: bottom drawer overlay }}
        <div class="fixed inset-0 z-50 flex flex-col justify-end">
          {{! Backdrop }}
          <div
            class="absolute inset-0 bg-black/50"
            role="button"
            tabindex="0"
            aria-label="Close menu"
            {{on "click" this.close}}
            {{on "keydown" this.onBackdropKeyDown}}
          ></div>

          {{! Sheet panel }}
          <div
            role="dialog"
            aria-modal="true"
            class="bg-base-100 relative z-10 max-h-[90dvh] w-full overflow-y-auto rounded-t-2xl shadow-2xl"
          >
            {{! Drag handle }}
            <div
              class="flex touch-none justify-center pt-3 pb-2"
              {{on "touchstart" this.onTouchStart}}
              {{on "touchmove" this.onTouchMove}}
              {{on "touchend" this.onTouchEnd}}
            >
              <div
                class="bg-base-300 h-1.5 w-12 cursor-grab rounded-full"
              ></div>
            </div>

            {{! Header }}
            <div class="flex items-center justify-between px-4 pb-2">
              {{#if @title}}
                <h3 class="text-base font-semibold">{{@title}}</h3>
              {{else}}
                <div></div>
              {{/if}}
              <button
                type="button"
                class="btn btn-ghost btn-sm btn-circle"
                aria-label="Close"
                {{on "click" this.close}}
              >
                <Icon @icon={{XIcon}} class="h-4 w-4" />
              </button>
            </div>

            {{! Content }}
            <div class="px-4 pb-[calc(env(safe-area-inset-bottom)+1rem)]">
              {{yield this.close to="content"}}
            </div>
          </div>
        </div>
      {{/if}}

    {{else}}
      {{! Desktop: standard DaisyUI dropdown }}
      <div class={{tw "dropdown" (if this.isOpen "dropdown-open") @placement}}>
        {{yield this.toggle this.isOpen to="trigger"}}

        {{#if this.isOpen}}
          <div
            tabindex="0"
            class={{tw
              "dropdown-content bg-base-100 rounded-box z-50 shadow-lg"
              @contentClass
            }}
          >
            {{yield this.close to="content"}}
          </div>
        {{/if}}
      </div>
    {{/if}}
  </template>
}
